import { Box } from "@mui/material";
import type { Dispatch, FC, SetStateAction } from "react";
import type { DepartmentLocation, HrisIntegrationType } from "../../../../utils/models";
import { FeatureFlagsTabContent } from "./FeatureFlagsTabContent";
import { GeneralTabContent } from "./GeneralTabContent";
import { HRISMappingTabContent } from "./hrisMapping/HRISMappingTabContent";
import { IntegrationsTabContent } from "./IntegrationsTabContent";
import { LocationsTabContent } from "./LocationsTabContent";
import { RatesAndRulesTabContent } from "./RatesAndRulesTabContent";
import { ShiftsAndRolesTabContent } from "./ShiftsAndRolesTabContent";
import { StaffAppTabContent } from "./StaffAppTabContent";
import type { GeneralFormData } from "./types";

export type DepartmentMetadataTab = "general" | "rates" | "shifts" | "staff-app" | "integrations" | "hris-mapping" | "feature-flags" | "locations";

// Renders the active tab's content; section navigation lives in the page header.
export const GeneralDepartmentMetadata: FC<{
	formData: GeneralFormData;
	setFormData: Dispatch<SetStateAction<GeneralFormData>>;
	/** Non-dirty-marking setter — only for values a server-side action already committed. See `useDeptConfig`. */
	syncFormData: Dispatch<SetStateAction<GeneralFormData>>;
	isNewDept: boolean;
	/** Whether the department's timezone is frozen. Derived once in `useDeptConfig` — never here. */
	isTimezoneLocked: boolean;
	/** True when the lock is a precaution because the has-shifts probe failed, not a real verdict. */
	isTimezoneLockUnknown: boolean;
	currentTab: DepartmentMetadataTab;
	isDirty: boolean;
	onRegisterNursaWebhook: () => void;
	isRegisteringNursaWebhook: boolean;
	onOpenHrisSummary: (summaryType: HrisIntegrationType) => void;
	existingDepartmentLocations: DepartmentLocation[];
	setExistingDepartmentLocations: Dispatch<SetStateAction<DepartmentLocation[]>>;
	newDepartmentLocations: DepartmentLocation[];
	setNewDepartmentLocations: Dispatch<SetStateAction<DepartmentLocation[]>>;
	setModifiedDepartmentLocations: Dispatch<SetStateAction<Set<string>>>;
	isUpdatingDepartmentLocations: boolean;
}> = ({
	formData,
	setFormData,
	syncFormData,
	isNewDept,
	isTimezoneLocked,
	isTimezoneLockUnknown,
	currentTab,
	isDirty,
	onRegisterNursaWebhook,
	isRegisteringNursaWebhook,
	onOpenHrisSummary,
	existingDepartmentLocations,
	setExistingDepartmentLocations,
	newDepartmentLocations,
	setNewDepartmentLocations,
	setModifiedDepartmentLocations,
	isUpdatingDepartmentLocations,
}) => {
	return (
		<Box>
			{currentTab === "general" && (
				<GeneralTabContent
					formData={formData}
					setFormData={setFormData}
					isNewDept={isNewDept}
					isTimezoneLocked={isTimezoneLocked}
					isTimezoneLockUnknown={isTimezoneLockUnknown}
				/>
			)}
			{currentTab === "rates" && <RatesAndRulesTabContent formData={formData} setFormData={setFormData} />}
			{currentTab === "shifts" && <ShiftsAndRolesTabContent formData={formData} setFormData={setFormData} isNewDept={isNewDept} />}
			{currentTab === "staff-app" && <StaffAppTabContent formData={formData} setFormData={setFormData} />}
			{currentTab === "integrations" && (
				<IntegrationsTabContent
					formData={formData}
					setFormData={setFormData}
					syncFormData={syncFormData}
					isDirty={isDirty}
					isNewDept={isNewDept}
					onRegisterNursaWebhook={onRegisterNursaWebhook}
					isRegisteringNursaWebhook={isRegisteringNursaWebhook}
					onOpenHrisSummary={onOpenHrisSummary}
				/>
			)}
			{currentTab === "hris-mapping" && !isNewDept && <HRISMappingTabContent />}
			{currentTab === "feature-flags" && <FeatureFlagsTabContent />}
			{currentTab === "locations" && (
				<LocationsTabContent
					formData={formData}
					setFormData={setFormData}
					isNewDept={isNewDept}
					existingDepartmentLocations={existingDepartmentLocations}
					setExistingDepartmentLocations={setExistingDepartmentLocations}
					newDepartmentLocations={newDepartmentLocations}
					setNewDepartmentLocations={setNewDepartmentLocations}
					setModifiedDepartmentLocations={setModifiedDepartmentLocations}
					isUpdatingDepartmentLocations={isUpdatingDepartmentLocations}
				/>
			)}
		</Box>
	);
};
